import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, Tag, Pencil } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import AdSenseUnit from "@/components/AdSenseUnit";
import BlogReactions from "@/components/BlogReactions";
import ContentPageFooter from "@/components/ContentPageFooter";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  body: string;
  tags: string[];
  earth_date: string;
  published: boolean;
  created_at: string;
}

const Blog = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase.from("blog_posts") as any)
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });
      setPosts(data || []);
      setLoading(false);
    })();
  }, []);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    posts.forEach((p) => p.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [posts]);

  const filtered = useMemo(() => {
    let result = posts;
    if (selectedTag) result = result.filter((p) => p.tags?.includes(selectedTag));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.earth_date?.toLowerCase().includes(q) ||
          p.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [posts, selectedTag, searchQuery]);

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-6 pb-24">
      <Helmet>
        <title>{t("blog.pageTitle", "Founder's Blog")} – Ayeris</title>
        <meta
          name="description"
          content="Updates, behind-the-scenes insights, and development notes from the founder of Ayeris."
        />
        <link rel="canonical" href="https://ayeris.tv/blog" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Founder's Blog",
            description:
              "Updates and insights from the founder of Ayeris.",
            url: "https://ayeris.tv/blog",
            publisher: {
              "@type": "Organization",
              name: "Ayeris",
              url: "https://ayeris.tv",
            },
          })}
        </script>
      </Helmet>

      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">{t("common.back", "Back")}</span>
      </button>

      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-display font-bold text-foreground">
          {t("blog.pageTitle", "Founder's Blog")}
        </h1>
        {isAdmin && (
          <button
            onClick={() => navigate("/blog/new")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> {t("blog.newPost", "New Post")}
          </button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {t("blog.pageSubtitle", "Updates, insights, and notes from behind the scenes.")}
      </p>

      {/* Search & Tag Filter */}
      <div className="space-y-3 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("blog.searchPlaceholder", "Search posts…")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedTag(null)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                !selectedTag
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("blog.all", "All")}
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                  selectedTag === tag
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                <Tag className="w-3 h-3" />
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Posts */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-xl bg-card border border-border animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 space-y-4">
          <p className="text-muted-foreground text-sm">
            {t("blog.noPosts", "No blog posts yet. Check back soon!")}
          </p>
          <p className="text-sm text-secondary-foreground leading-relaxed max-w-md mx-auto">
            {t("blog.emptyIntro", "The Founder's Blog is where we share development updates, behind-the-scenes stories, and thoughts on building Ayeris — a swipe-based movie and TV discovery app designed to end endless scrolling.")}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {filtered.map((post, idx) => (
            <div key={post.id}>
              <article className="p-5 rounded-xl bg-card border border-border space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    {post.earth_date && (
                      <p className="text-xs text-primary font-mono mb-1">
                        {post.earth_date}
                      </p>
                    )}
                    <h2 className="text-lg font-semibold text-foreground leading-snug">
                      {post.title}
                    </h2>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => navigate(`/blog/new?id=${post.id}`)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition shrink-0"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {post.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div
                  className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground [&_p]:mb-4 [&_br]:block [&_br]:mb-2"
                  dangerouslySetInnerHTML={{ __html: post.body }}
                />
                <BlogReactions postId={post.id} />
              </article>
              {/* Ad after 2nd post */}
              {idx === 1 && filtered.length > 2 && (
                <div className="my-4">
                  <AdSenseUnit adSlot="5004064879" className="rounded-xl overflow-hidden" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bottom ad — only show when there's actual content */}
      {filtered.length > 0 && (
        <div className="mt-8">
          <AdSenseUnit adSlot="5004064879" className="rounded-xl overflow-hidden" />
        </div>
      )}

      <ContentPageFooter currentPath="/blog" />
    </div>
  );
};

export default Blog;
